import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, 
    Alert, 
    Button, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Typography, 
    Chip, 
    IconButton, 
    CircularProgress, 
    Paper, 
    TablePagination 
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { convertCsvToExportFormat } from '../../../utils/exportUtils';
import { LoginService } from '../../../api/services/loginService';
import { extractTotalCount, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function ActiveSessionsTab({ setNotification }) {
    const [sessions, setSessions] = useState([]);
    const [sessionsTotal, setSessionsTotal] = useState(0);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsError, setSessionsError] = useState('');
    const [sessionsPage, setSessionsPage] = useState(0);
    const [sessionsRowsPerPage, setSessionsRowsPerPage] = useState(20);
    const [exportSessionsModalOpen, setExportSessionsModalOpen] = useState(false);

    const fetchSessions = useCallback(async () => {
        setSessionsLoading(true);
        setSessionsError('');
        try {
            const data = await LoginService.getActiveSessions({
                page: sessionsPage,
                limit: sessionsRowsPerPage
            });
            const sessionsList = data?.data?.sessions || data?.sessions || data?.data || data || [];
            setSessions(Array.isArray(sessionsList) ? sessionsList : []);
            setSessionsTotal(extractTotalCount(data, sessionsList));
        } catch (err) {
            setSessionsError(err?.response?.data?.message || err.message || 'Failed to fetch sessions');
        } finally {
            setSessionsLoading(false);
        }
    }, [sessionsPage, sessionsRowsPerPage]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const handleTerminate = async (sessionId) => {
        try {
            await LoginService.terminateSession(sessionId);
            setNotification({ open: true, message: 'Session terminated successfully', severity: 'success' });
            fetchSessions();
        } catch (err) {
            setNotification({ open: true, message: err?.response?.data?.message || err.message || 'Failed to terminate session', severity: 'error' });
        }
    };

    const [exportSessionsLoading, setExportSessionsLoading] = useState(false);

    const handleExportSessions = async (format) => {
        setExportSessionsModalOpen(false);
        setExportSessionsLoading(true);
        try {
            const response = await LoginService.exportActiveSessions();
            
            const csvText = await response.data.text();
            
            if (!csvText || csvText.trim() === '') {
                setNotification({ open: true, message: 'No active sessions to export', severity: 'warning' });
                return;
            }
            
            const filenameBase = `active-sessions-${new Date().toISOString().split('T')[0]}`;
            await convertCsvToExportFormat(csvText, format, filenameBase);
            setNotification({ open: true, message: 'Sessions exported successfully', severity: 'success' });
        } catch (err) {
            setNotification({ open: true, message: err?.response?.data?.message || err.message || 'Failed to export sessions', severity: 'error' });
        } finally {
            setExportSessionsLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Alert severity="info" sx={{ flexGrow: 1, mr: 2, mb: 0 }}>
                    Manage active administrative sessions. Terminating a session will log the user out immediately.
                </Alert>
                <Button 
                    variant="outlined" 
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setExportSessionsModalOpen(true)}
                    sx={standardSecondaryButtonSx}
                >
                    Export
                </Button>
            </Box>
            <ExportFormatModal 
                open={exportSessionsModalOpen}
                onClose={() => setExportSessionsModalOpen(false)}
                onExport={handleExportSessions}
                isExporting={exportSessionsLoading}
                title="Export Active Sessions"
            />
            {sessionsError && <Alert severity="error" sx={{ mb: 2 }}>{sessionsError}</Alert>}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Device / Browser</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>Last Activity</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessionsLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : sessions.length > 0 ? (
                            sessions.map((session) => (
                                <TableRow key={session.id || session._id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {typeof session.user === 'object' ? (session.user?.name || session.user?.email || 'N/A') : (session.user || session.userName || 'N/A')}
                                        </Typography>
                                        {session.current && <Chip label="Current Session" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem', ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{session.role || 'N/A'}</TableCell>
                                    <TableCell>{session.device || session.deviceInfo || session.userAgent || 'N/A'}</TableCell>
                                    <TableCell>{session.ip || session.ipAddress || 'N/A'}</TableCell>
                                    <TableCell>{session.lastActive || session.lastActivity ? new Date(session.lastActive || session.lastActivity).toLocaleString() : 'N/A'}</TableCell>
                                    <TableCell align="right">
                                        {!session.current && (
                                            <IconButton color="error" onClick={() => handleTerminate(session.id || session._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body2" color="text.secondary">No active sessions found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {sessionsTotal > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        component="div"
                        count={sessionsTotal}
                        rowsPerPage={sessionsRowsPerPage}
                        page={sessionsPage}
                        onPageChange={(e, newPage) => setSessionsPage(newPage)}
                        onRowsPerPageChange={(e) => { setSessionsRowsPerPage(parseInt(e.target.value, 10)); setSessionsPage(0); }}
                    />
                )}
            </TableContainer>
        </Box>
    );
}
